import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../shared/context/UserContext";
import { getSites } from "../inventory/api/inventoryApi";
import { parseImportFile, validateImport, commitImport } from "./dataExchangeApi";
import ColumnMapper from "./ColumnMapper";
import ValidationPreview from "./ValidationPreview";

const STEPS = ["Upload", "Map Columns", "Validate", "Commit", "Result"];

export default function ImportWizard() {
  const { callerEmail } = useUser();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getSites(callerEmail).then(data => {
      const list = Array.isArray(data) ? data : data.sites || [];
      setSites(list);
      if (list.length === 1) setSelectedSiteId(list[0].site_id);
    }).catch(() => {});
  }, [callerEmail]);

  function siteLabel(site) {
    if (site.site_city) return `${site.site_name} (${site.site_city})`;
    if (site.supplier_site_code) return `${site.site_name} (${site.supplier_site_code})`;
    return site.site_name || "Unnamed Site";
  }

  const selectedSiteName = sites.find(s => s.site_id === selectedSiteId);

  // --- Step 1: Parse ---
  async function handleParse() {
    setLoading(true);
    setError(null);
    try {
      const result = await parseImportFile({ file, siteId: selectedSiteId, callerEmail });
      setParseResult(result);
      setSuggestions(result.suggested_mappings || {});
      setMapping({});
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // --- Step 2 → 3: Validate ---
  async function handleValidate() {
    setLoading(true);
    setError(null);
    try {
      const result = await validateImport({
        blobName: parseResult.blob_name,
        siteId: selectedSiteId,
        mapping: { ...suggestions, ...mapping },
        callerEmail,
      });
      setValidationResult(result);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // --- Step 3 → 4 → 5: Commit ---
  const handleCommit = useCallback(async () => {
    setStep(4);
    setLoading(true);
    setError(null);
    try {
      const result = await commitImport({
        blobName: parseResult.blob_name,
        siteId: selectedSiteId,
        mapping: { ...suggestions, ...mapping },
        callerEmail,
      });
      setCommitResult(result);
      setStep(5);
    } catch (err) {
      setError(err.message);
      // Stay on step 4 with error
    } finally {
      setLoading(false);
    }
  }, [parseResult, selectedSiteId, mapping, suggestions, callerEmail]);

  // --- Reset ---
  function handleReset() {
    setStep(1);
    setFile(null);
    setParseResult(null);
    setMapping({});
    setSuggestions({});
    setValidationResult(null);
    setCommitResult(null);
    setError(null);
  }

  // --- Drag and drop ---
  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith(".xlsx")) {
      setFile(droppedFile);
    }
  }

  function handleFileSelect(e) {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  }

  // --- Stepper click ---
  function handleStepClick(targetStep) {
    if (targetStep >= step) return;
    if (!parseResult && targetStep > 1) return;
    setStep(targetStep);
    setError(null);
  }

  // --- Step 5: commit outcome verdict ---
  // Sums EVERY counter the backend returns, not just the ones rendered — a run
  // that only touched asset tags or disposal workflows still wrote something,
  // and must not be reported as a total failure.
  const sumCounters = obj =>
    Object.values(obj || {}).reduce((total, n) => total + (Number(n) || 0), 0);
  const wroteCount = commitResult
    ? sumCounters(commitResult.created) + sumCounters(commitResult.updated)
    : 0;
  const errorCount = commitResult?.errors?.length ?? 0;
  const resultVerdict =
    errorCount === 0
      ? { text: "Import Complete", cls: "" }
      : wroteCount > 0
        ? { text: "Import Completed with Errors", cls: "iw-result-header-warning" }
        : { text: "Import Failed", cls: "iw-result-header-error" };

  return (
    <div>
      {/* Stepper */}
      <div className="iw-stepper">
        {STEPS.map((label, idx) => {
          const num = idx + 1;
          const isCompleted = num < step;
          const isCurrent = num === step;
          const canClick = num < step && (num === 1 || parseResult);
          return (
            <React.Fragment key={label}>
              {idx > 0 && <div className={`iw-step-line ${isCompleted ? "completed" : ""}`} />}
              <button
                className={`iw-step ${isCurrent ? "current" : ""} ${isCompleted ? "completed" : ""}`}
                onClick={() => canClick && handleStepClick(num)}
                disabled={!canClick}
              >
                <span className="iw-step-num">
                  {isCompleted ? "\u2713" : num}
                </span>
                <span className="iw-step-label">{label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Error banner */}
      {error && step !== 4 && (
        <div className="de-banner de-banner-error" style={{ marginBottom: "var(--space-3)" }}>
          {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="de-panel">
          <label className="de-label">Site</label>
          <select className="de-select" value={selectedSiteId} onChange={e => setSelectedSiteId(e.target.value)}>
            <option value="">Select a site...</option>
            {sites.map(s => (
              <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>
            ))}
          </select>

          <label className="de-label" style={{ marginTop: "var(--space-4)" }}>Upload File</label>
          <div
            className={`iw-dropzone ${dragOver ? "dragover" : ""} ${file ? "has-file" : ""}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            {file ? (
              <div className="iw-dropzone-file">
                {file.name} ({(file.size / 1024).toFixed(0)} KB)
                <button
                  className="iw-dropzone-remove"
                  onClick={e => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                >
                  ✕ Remove
                </button>
              </div>
            ) : (
              <>
                <div className="iw-dropzone-text">Drag and drop .xlsx file here</div>
                <div className="iw-dropzone-sub">or click to browse</div>
              </>
            )}
          </div>
          <div className="iw-dropzone-hint">Accepted: .xlsx only. Max 50MB.</div>

          <div className="iw-footer">
            <div />
            <button
              className="btn btn-primary"
              disabled={!selectedSiteId || !file || loading}
              onClick={handleParse}
            >
              {loading ? "Uploading..." : "Next \u2192"}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapper */}
      {step === 2 && parseResult && (
        <ColumnMapper
          parseResult={parseResult}
          mapping={mapping}
          setMapping={setMapping}
          suggestions={suggestions}
          siteName={selectedSiteName ? siteLabel(selectedSiteName) : selectedSiteId}
          onNext={handleValidate}
          onBack={() => { setStep(1); setError(null); }}
          loading={loading}
        />
      )}

      {/* Step 3: Validation Preview */}
      {step === 3 && validationResult && (
        <ValidationPreview
          validationResult={validationResult}
          onCommit={handleCommit}
          onBack={() => { setStep(2); setError(null); }}
          loading={loading}
        />
      )}

      {/* Step 4: Commit in progress */}
      {step === 4 && (
        <div className="de-panel iw-commit-loading">
          {loading ? (
            <>
              <div className="iw-spinner" />
              <h3>Committing import...</h3>
              <p style={{ color: "var(--color-text-secondary)" }}>
                Writing tools, part numbers, and lifecycle profiles.<br />
                Please don't close this tab.
              </p>
            </>
          ) : error ? (
            <>
              <div className="de-banner de-banner-error">{error}</div>
              <div className="iw-footer" style={{ marginTop: "var(--space-3)" }}>
                <button className="btn btn-secondary" onClick={() => { setStep(3); setError(null); }}>
                  &larr; Back to Validation
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Step 5: Result */}
      {step === 5 && commitResult && (
        <div className="de-panel">
          <div className={`iw-result-header${resultVerdict.cls ? ` ${resultVerdict.cls}` : ""}`}>
            {resultVerdict.text}
          </div>

          {errorCount > 0 && (
            <div className="vp-issues">
              <div className="vp-issues-header vp-issues-error">
                ERRORS &mdash; {errorCount}
              </div>
              {commitResult.errors.map((e, i) => (
                <div key={i} className="vp-issue-row vp-issue-error">
                  <span>{e.message}</span>
                </div>
              ))}
            </div>
          )}

          <div className="iw-result-section">
            <h4>Created</h4>
            <ul className="iw-result-list">
              <li>{commitResult.created.tools} tools</li>
              <li>{commitResult.created.lifecycle_profiles} lifecycle profiles</li>
              <li>{commitResult.created.disposal_workflows} disposal workflows</li>
              <li>{commitResult.created.part_numbers} part numbers</li>
              <li>{commitResult.created.asset_tags} asset tags</li>
            </ul>
          </div>

          <div className="iw-result-section">
            <h4>Updated</h4>
            <ul className="iw-result-list">
              <li>{commitResult.updated.tools} tools</li>
              <li>{commitResult.updated.lifecycle_profiles} lifecycle profiles</li>
              <li>{commitResult.updated.disposal_workflows} disposal workflows</li>
              <li>{commitResult.updated.part_numbers} part numbers</li>
              <li>{commitResult.updated.asset_tags} asset tags</li>
            </ul>
          </div>

          {/* U-5. Matched, compared, and not written because nothing differed.
              Optional-chained: a backend that predates U-5 omits the key, and
              the section simply does not render rather than throwing.
              Tools only -- the child containers are not guarded yet, so a zero
              for them would mean "not measured", not "none unchanged", and the
              backend deliberately sends a single-key object. */}
          {commitResult.unchanged && (
            <div className="iw-result-section">
              <h4>Unchanged</h4>
              <ul className="iw-result-list">
                <li>{commitResult.unchanged.tools} tools already matched the file</li>
              </ul>
            </div>
          )}

          <div className="iw-footer" style={{ marginTop: "var(--space-4)" }}>
            <button className="btn btn-secondary" onClick={handleReset}>
              Import Another File
            </button>
            <button
              className={`btn ${wroteCount === 0 ? "btn-secondary" : "btn-primary"}`}
              onClick={() => navigate("/registry")}
            >
              Go to Asset Registry &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
